import { inject, InjectionToken, Pipe, type PipeTransform } from '@angular/core';
import { ValidationError } from '@angular/forms/signals';
import { TranslocoService } from '@jsverse/transloco';

/**
 * Maps a validator key to a transloco key and an optional params extractor.
 * Keys are resolved within the nested `validations` object of the global scope.
 *
 * @example
 * ```ts
 * const rules: ValidationMessageRules = {
 *   required: { key: 'Validations.Required' },
 *   maxlength: {
 *     key: 'Validations.MaxLength',
 *     params: (err) => ({ max: err.requiredLength }),
 *   },
 * };
 * ```
 */
export interface ValidationMessageRule {
  key: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  params?: (error: any) => Record<string, unknown>;
}

export type ValidationMessageRules = Record<string, ValidationMessageRule>;

/**
 * Default rules for built-in validators.
 *
 * Covers BOTH form systems so messages survive the gradual Signal Forms
 * migration: classic Reactive Forms emit lowercase keys (`minlength`,
 * `maxlength`) carrying a `requiredLength` payload, while Signal Forms
 * (`@angular/forms/signals`) emit `kind`-cased keys (`minLength`, `maxLength`)
 * whose limit lives on a same-named property. The `required`, `min`, `max`,
 * `email` and `pattern` keys are shared verbatim — both systems agree on the
 * casing and on where the numeric limit sits.
 *
 * Override or extend via `provideValidationMessages()`.
 */
export const DEFAULT_VALIDATION_RULES: ValidationMessageRules = {
  required: { key: 'Validations.Required' },
  // Classic Reactive Forms keys.
  minlength: {
    key: 'Validations.MinLength',
    params: (err: { requiredLength: number }) => ({ min: err.requiredLength }),
  },
  maxlength: {
    key: 'Validations.MaxLength',
    params: (err: { requiredLength: number }) => ({ max: err.requiredLength }),
  },
  // Signal Forms keys (validation error `kind`).
  minLength: {
    key: 'Validations.MinLength',
    params: (err: { minLength: number }) => ({ min: err.minLength }),
  },
  maxLength: {
    key: 'Validations.MaxLength',
    params: (err: { maxLength: number }) => ({ max: err.maxLength }),
  },
  min: {
    key: 'Validations.Min',
    params: (err: { min: number }) => ({ min: err.min }),
  },
  max: {
    key: 'Validations.Max',
    params: (err: { max: number }) => ({ max: err.max }),
  },
  email: { key: 'Validations.Email' },
  pattern: { key: 'Validations.Pattern' },
};

export const VALIDATION_MESSAGE_RULES = new InjectionToken<ValidationMessageRules>(
  'VALIDATION_MESSAGE_RULES',
  { providedIn: 'root', factory: () => DEFAULT_VALIDATION_RULES },
);

/**
 * Provide custom validation message rules, merged with defaults.
 *
 * @example
 * ```ts
 * // app.config.ts
 * provideValidationMessages({
 *   uniqueEmail: { key: 'Validations.UniqueEmail' },
 *   compare: { key: 'Validations.Compare' },
 * })
 * ```
 */
export function provideValidationMessages(rules: ValidationMessageRules) {
  return {
    provide: VALIDATION_MESSAGE_RULES,
    useValue: { ...DEFAULT_VALIDATION_RULES, ...rules },
  };
}

/** Loose structural shape — for unit-test fixtures and dynamic param access. */
interface LooseValidationError {
  kind: string;
  [key: string]: unknown;
}

/**
 * A Signal Forms (`@angular/forms/signals`) validation error: a `kind` (the
 * validator that failed, e.g. `'required'`, `'maxLength'`) plus its payload
 * (e.g. `maxLength: 64`).
 *
 * A UNION on purpose. `ValidationError.WithFieldTree` is what `field().errors()`
 * yields in form templates — a SEALED type with no index signature. A type
 * without its own index signature is NOT assignable to a loose
 * `{ [key: string]: unknown }` (even when every property would satisfy
 * `unknown`), so a loose supertype alone breaks the template type-check;
 * `WithFieldTree` alone breaks the unit tests (plain `{ kind, maxLength }`
 * fixtures don't satisfy the sealed shape). The union accepts both. `message`
 * is read through a local `as { message?: string }` cast (Signal Forms types it
 * `string | LogicFn<…>`, not assignable to a narrow `string`).
 */
export type FormValidationError = ValidationError.WithFieldTree | LooseValidationError;

/**
 * Impure pipe that resolves a single Signal Forms validation error to a
 * translated message, reusing the same `VALIDATION_MESSAGE_RULES` as the
 * classic {@link ValidationMessageLegacyPipe}. The schema declares only the rules
 * (no message); the message is keyed off `error.kind` and translated via
 * transloco — so i18n stays in one place and stays reactive to language change.
 *
 * Falls back to the schema-provided `error.message` (if any), then to the
 * generic `Validations.Default` key.
 *
 * @example
 * ```html
 * @for (error of form.name().errors(); track error.kind) {
 *   <hlm-field-error [validator]="error.kind">{{ error | validationMessage }}</hlm-field-error>
 * }
 * ```
 */
@Pipe({ name: 'validationMessage', pure: false })
export class ValidationMessagePipe implements PipeTransform {
  private readonly transloco = inject(TranslocoService);
  private readonly rules = inject(VALIDATION_MESSAGE_RULES);

  // Cache fields — mirrors the memoization in ValidationMessageLegacyPipe.
  // Cache key: (error object reference, active language, resolved params signature).
  // The params signature detects mutations on the same error object reference
  // (e.g. a dynamically-updated maxLength) that would produce a stale message
  // if only the reference and language were compared.
  private lastError: FormValidationError | null = null;
  private lastLang = '';
  private lastParamsSig = '';
  private lastMessage = '';

  transform(error: FormValidationError | null | undefined): string {
    if (!error) {
      this.lastError = null;
      this.lastLang = '';
      this.lastParamsSig = '';
      this.lastMessage = '';
      return '';
    }

    const lang = this.transloco.getActiveLang();
    const rule = this.rules[error.kind];

    // params() and JSON.stringify() can both throw for custom validators
    // (e.g. BigInt values or circular references passed via provideValidationMessages).
    // A throw here would propagate into Angular change detection and crash the view.
    // Fall back to a reference-only sentinel so the cache still invalidates on
    // new error objects; the translate() call below will still resolve the message.
    let params: Record<string, unknown>;
    let paramsSig: string;
    try {
      params = rule?.params ? rule.params(error) : {};
      paramsSig = JSON.stringify(params);
    } catch {
      params = {};
      paramsSig = `__unserializable__${String(error)}`;
    }

    if (error === this.lastError && lang === this.lastLang && paramsSig === this.lastParamsSig) {
      return this.lastMessage;
    }

    const message: string = rule
      ? (this.transloco.translate(rule.key, params) ?? '')
      : ((error as { message?: string }).message ??
        this.transloco.translate('Validations.Default') ??
        '');

    // Assign cache fields ONLY after translate() succeeds — avoids poisoning
    // the cache if translate() throws for any reason.
    this.lastError = error;
    this.lastLang = lang;
    this.lastParamsSig = paramsSig;
    this.lastMessage = message;

    return this.lastMessage;
  }
}
